import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, LayoutTemplate, Image as ImageIcon, Megaphone, Plus, Circle, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { SpotlightCreative } from '@/components/spotlights/SpotlightCreative';

const SAMPLE = {
  title: 'Hand-blended flower essences',
  body: 'Small-batch remedies, made to order in Vermont.',
  cta_label: 'Visit the shop',
  cta_type: 'internal',
  label: 'Sponsored',
  image_url: 'https://static.prod-images.emergentagent.com/jobs/466e3310-a725-4cde-af13-49d0bcf09ec9/images/2a2090bc12a1439fe920c69f9f63c882b779249e304a9f97a90d368b3c4b224b.jpeg',
};

/** The space as a member sees it — real creative markup, on the real page background. */
function SpacePreview({ space }) {
  const ad = (space.running || [])[0];
  const light = space.theme === 'light';
  return (
    <div className="rounded-lg overflow-hidden border border-white/10" data-testid={`space-preview-${space.key}`}>
      <p className="px-3 py-1.5 text-[11px] text-gray-400 bg-black/30">
        {ad ? 'What is running here now' : 'How an ad would look here'} · {light ? 'light page' : 'dark page'}
      </p>
      <div className={`p-4 ${light ? 'bg-[#faf7f2]' : 'bg-[#0f1116]'} ${space.variant === 'flank' ? 'flex' : ''}`}>
        <SpotlightCreative
          p={ad || SAMPLE}
          variant={space.variant || 'banner'}
          theme={space.theme || 'dark'}
          className={space.variant === 'flank' ? 'w-56' : space.variant === 'card' ? 'max-w-xs' : ''}
        />
      </div>
    </div>
  );
}

/**
 * Admin · Ad Spaces — the seven spaces on one screen: what is running in each, what it costs,
 * the artwork it needs, a real preview, and whether an empty one invites practitioners and
 * vendors to ask for it. Built because nothing in the admin area showed the spaces themselves.
 */
export default function AdminAdSpaces({ onFillSpace }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openInvite, setOpenInvite] = useState(null);
  const [openPreview, setOpenPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/spotlights/spaces`, { headers });
      setSpaces(res.data.spaces || []);
    } catch (err) {
      toast.error('Could not load your ad spaces');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (key, path, value) =>
    setSpaces((prev) => prev.map((s) => s.key === key
      ? { ...s, [path]: { ...s[path], ...value } }
      : s));

  const saveAll = async () => {
    setSaving(true);
    try {
      const prices = {};
      const invites = {};
      spaces.forEach((s) => {
        prices[s.key] = {
          price_per_day: parseFloat(s.pricing?.price_per_day) || 0,
          price_per_month: parseFloat(s.pricing?.price_per_month) || 0,
          enabled: !!s.pricing?.enabled,
        };
        invites[s.key] = {
          enabled: !!s.invite?.enabled,
          headline: s.invite?.headline || '',
          body: s.invite?.body || '',
          cta_label: s.invite?.cta_label || 'Request this space',
          image_url: s.invite?.image_url || '',
        };
      });
      await Promise.all([
        axios.post(`${API}/admin/spotlight-promotions/pricing`, { prices }, { headers }),
        axios.put(`${API}/admin/spotlights/space-invites`, { invites }, { headers }),
      ]);
      toast.success('Ad spaces saved');
      load();
    } catch (err) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;

  return (
    <div className="space-y-3" data-testid="admin-ad-spaces">
      <p className="text-sm text-gray-400">
        Each space below is one place an ad can appear. Green means something is running there
        right now; grey means the space is empty.
      </p>

      {spaces.map((s) => {
        const running = s.running || [];
        const paid = running.filter((r) => r.source !== 'house');
        const filler = running.filter((r) => r.fill_only);
        const isEmpty = running.length === 0;
        return (
          <div key={s.key} className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-3"
            data-testid={`ad-space-${s.key}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Circle className={`w-2.5 h-2.5 ${isEmpty ? 'text-gray-500' : 'text-emerald-400'}`}
                    fill="currentColor" />
                  <p className="text-gray-100 font-medium">{s.label}</p>
                  {!s.pricing?.enabled && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400">
                      Not for sale
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1" data-testid={`ad-space-status-${s.key}`}>
                  {isEmpty
                    ? 'Empty — nothing is showing here'
                    : running.map((r) => `${r.title}${r.fill_only ? ' (fills gaps)' : ''}`).join(' · ')}
                </p>
                {paid.length > 0 && (
                  <p className="text-[11px] text-emerald-300/80 mt-0.5">{paid.length} paid ad running</p>
                )}
                {filler.length > 0 && paid.length === 0 && (
                  <p className="text-[11px] text-amber-300/80 mt-0.5">Your own promo is holding the space</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" className="h-8 text-gray-300"
                  onClick={() => setOpenPreview(openPreview === s.key ? null : s.key)}
                  data-testid={`preview-space-${s.key}`}>
                  <Eye className="w-3.5 h-3.5 mr-1" /> {openPreview === s.key ? 'Hide preview' : 'Preview'}
                </Button>
                <Button size="sm" variant="outline"
                  className="h-8 border-amber-400/40 text-amber-200 hover:bg-amber-500/10"
                  onClick={() => onFillSpace?.(s.key)}
                  data-testid={`fill-space-${s.key}`}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Put a promo here
                </Button>
              </div>
            </div>

            {openPreview === s.key && <SpacePreview space={s} />}

            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-1">
                <span className="text-gray-400 text-sm">$</span>
                <Input type="number" min="0" step="0.01" value={s.pricing?.price_per_day ?? 0}
                  onChange={(e) => patch(s.key, 'pricing', { price_per_day: e.target.value })}
                  className="w-24 bg-white/5 border-white/10 h-8"
                  data-testid={`space-price-day-${s.key}`} />
                <span className="text-gray-400 text-xs">/day</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-400 text-sm">$</span>
                <Input type="number" min="0" step="0.01" value={s.pricing?.price_per_month ?? 0}
                  onChange={(e) => patch(s.key, 'pricing', { price_per_month: e.target.value })}
                  className="w-24 bg-white/5 border-white/10 h-8"
                  data-testid={`space-price-month-${s.key}`} />
                <span className="text-gray-400 text-xs">/month</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!s.pricing?.enabled}
                  onCheckedChange={(v) => patch(s.key, 'pricing', { enabled: v })}
                  data-testid={`space-for-sale-${s.key}`} />
                <span className="text-xs text-gray-400">For sale</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!s.invite?.enabled}
                  onCheckedChange={(v) => { patch(s.key, 'invite', { enabled: v }); if (v) setOpenInvite(s.key); }}
                  data-testid={`space-invite-toggle-${s.key}`} />
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Megaphone className="w-3 h-3 text-amber-300/70" /> Invite requests when empty
                </span>
              </div>
              {s.invite?.enabled && (
                <button className="text-xs text-amber-300 hover:text-amber-200 underline"
                  onClick={() => setOpenInvite(openInvite === s.key ? null : s.key)}
                  data-testid={`space-invite-edit-${s.key}`}>
                  {openInvite === s.key ? 'Hide wording' : 'Edit wording'}
                </button>
              )}
            </div>

            {s.invite?.enabled && openInvite === s.key && (
              <div className="rounded-lg bg-black/20 border border-white/10 p-3 space-y-2"
                data-testid={`space-invite-fields-${s.key}`}>
                <p className="text-[11px] text-gray-400">
                  Practitioners and vendors see this card when the space is empty. The button opens
                  a short request form — nothing is charged, and the request comes to you.
                </p>
                <div>
                  <Label className="text-xs text-gray-400">Headline</Label>
                  <Input value={s.invite?.headline || ''}
                    onChange={(e) => patch(s.key, 'invite', { headline: e.target.value })}
                    className="bg-white/5 border-white/10 mt-1 h-8"
                    data-testid={`space-invite-headline-${s.key}`} />
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Sub-line</Label>
                  <Input value={s.invite?.body || ''}
                    onChange={(e) => patch(s.key, 'invite', { body: e.target.value })}
                    className="bg-white/5 border-white/10 mt-1 h-8"
                    data-testid={`space-invite-body-${s.key}`} />
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Button</Label>
                  <Input value={s.invite?.cta_label || ''}
                    onChange={(e) => patch(s.key, 'invite', { cta_label: e.target.value })}
                    className="bg-white/5 border-white/10 mt-1 h-8"
                    data-testid={`space-invite-cta-${s.key}`} />
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Image (a preset one is already in place)</Label>
                  <Input value={s.invite?.image_url || ''}
                    onChange={(e) => patch(s.key, 'invite', { image_url: e.target.value })}
                    placeholder="https://…  — leave blank for no image"
                    className="bg-white/5 border-white/10 mt-1 h-8"
                    data-testid={`space-invite-image-${s.key}`} />
                  {s.invite?.image_url && (
                    <img src={s.invite.image_url} alt="" className="mt-2 h-14 rounded object-cover" />
                  )}
                </div>
              </div>
            )}

            {s.image && (
              <p className="text-[11px] text-gray-400 flex items-start gap-1.5">
                <ImageIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-cyan-300" />
                <span>
                  <strong className="text-gray-300">{s.image.width}×{s.image.height}px</strong>{' '}
                  ({s.image.ratio}), under {s.image.max_kb}KB — {s.image.note}
                </span>
              </p>
            )}
          </div>
        );
      })}

      <Button onClick={saveAll} disabled={saving}
        className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-ad-spaces-btn">
        {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <LayoutTemplate className="w-4 h-4 mr-1" />}
        Save ad spaces
      </Button>
    </div>
  );
}
